/**
 * Setup progress for the signed-in org: the facts from
 * `GET /partner/setup-progress` plus the steps this browser pressed Continue
 * on, turned into done / to do / optional by setup/status.ts. The facts are
 * re-read on every navigation, so publishing on one step shows on the rail as
 * soon as the partner moves on.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { SetupProgress } from '../api/types';
import { useSession } from '../auth/session';
import { useRouter } from '../router';
import { useSites } from '../state/sites';
import { useQuery } from '../ui/hooks';
import { stepsFor, type SetupStep } from './steps';
import { continueTarget, installState, isStepDone, remainingSteps, stepStatus, type InstallState, type StepStatus } from './status';

interface ProgressValue {
  isDone: (slug: string) => boolean;
  statusOf: (step: SetupStep) => StepStatus;
  /** Required steps still to do, for this role, in order. */
  remaining: SetupStep[];
  /** The first incomplete required step's path, or null. */
  nextPath: string | null;
  markDone: (slug: string) => void;
  /** Undo a Continue mark (a step the data decides stays as the data says). */
  unmarkDone: (slug: string) => void;
  /** Re-read the facts now (e.g. the moment a test lead arrives). */
  refresh: () => void;
  /** Installed / live / checked / owned for the current site (status.ts `installState`); null until the facts load. */
  install: InstallState | null;
}

const ProgressContext = createContext<ProgressValue>({
  isDone: () => false,
  statusOf: (s) => (s.optional ? 'optional' : 'todo'),
  remaining: [],
  nextPath: null,
  markDone: () => {},
  unmarkDone: () => {},
  refresh: () => {},
  install: null,
});

export function ProgressProvider({ children }: { children: ReactNode }) {
  const { api, org, role } = useSession();
  const { current } = useSites();
  const { path } = useRouter();
  const key = `fx-partner-setup-${org?.id ?? 'none'}`;
  const [marks, setMarks] = useState<string[]>(() => {
    try {
      return JSON.parse(window.localStorage.getItem(key) ?? '[]') as string[];
    } catch {
      return [];
    }
  });
  const [tick, setTick] = useState(0);
  const siteKey = current?.live.site_key;
  const facts = useQuery(() => api.setupProgress(siteKey), [siteKey, path, tick], { enabled: stepsFor(role).length > 0 });
  // A failed read (or an API from before the route) falls back to the marks;
  // stale facts from a previous read are still better than none.
  const data: SetupProgress | null = facts.data ?? null;
  // Without facts, an impression the site list already knows about still counts (the old rule).
  const impression = Boolean(current?.live.last_impression_at);
  const effective = useMemo(() => (data || !impression || marks.includes('verify') ? marks : [...marks, 'verify']), [data, impression, marks]);

  const update = useCallback(
    (change: (prev: string[]) => string[]) => {
      setMarks((prev) => {
        const next = change(prev);
        if (next === prev) return prev;
        try {
          window.localStorage.setItem(key, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    [key],
  );
  const markDone = useCallback((slug: string) => update((prev) => (prev.includes(slug) ? prev : [...prev, slug])), [update]);
  const unmarkDone = useCallback((slug: string) => update((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : prev)), [update]);
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  const value = useMemo<ProgressValue>(
    () => ({
      isDone: (slug) => isStepDone(slug, data, effective),
      statusOf: (step) => stepStatus(step, data, effective),
      remaining: remainingSteps(role, data, effective),
      nextPath: continueTarget(role, data, effective),
      markDone,
      unmarkDone,
      refresh,
      install: data ? installState(data) : null,
    }),
    [data, effective, role, markDone, unmarkDone, refresh],
  );
  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
}

export const useProgress = () => useContext(ProgressContext);
